import React, { useState } from "react";
import { motion } from "framer-motion";
import { FilePlus2, Trash2, Settings2, Lock } from "lucide-react";
import { useAuth } from "./context/AuthContext";
import { CARD, PageNav } from "./components/PageChrome";
import Register from "./pages/Register";
import Deregister from "./pages/Deregister";
import Modify from "./pages/Modify";
import Onboarding from "./pages/Onboarding";

// Same shades as the action buttons on the Register, Modify and Deregister pages.
const CARD_PALETTES = {
  green: {
    border: "border-green-500",
    button: "bg-green-50 text-green-700 hover:bg-green-600 hover:text-white shadow-md shadow-green-200/80 hover:shadow-lg hover:shadow-green-300/70",
  },
  amber: {
    border: "border-amber-500",
    button: "bg-amber-50 text-amber-700 hover:bg-amber-600 hover:text-white shadow-md shadow-amber-200/80 hover:shadow-lg hover:shadow-amber-300/70",
  },
  red: {
    border: "border-red-500",
    button: "bg-red-50 text-red-700 hover:bg-red-600 hover:text-white shadow-md shadow-red-200/80 hover:shadow-lg hover:shadow-red-300/70",
  },
};

function ActionCard({ title, desc, icon: Icon, color = "green", onClick, cta, disabled = false }) {
  const s = CARD_PALETTES[color] || CARD_PALETTES.green;
  const lockHint = "Requires an editor or administrator role";
  return (
    <div
      className={`relative bg-white border-2 rounded-2xl p-7 flex flex-col justify-between min-h-[280px] shadow-sm ${disabled ? "border-slate-200 opacity-60" : s.border}`}
    >
      {disabled && (
        <div className="absolute top-4 right-4 text-slate-400" title={lockHint}>
          <Lock size={16} />
        </div>
      )}
      <div>
        <h3 className="text-sm font-black tracking-[0.15em] mb-4 text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
      </div>
      <div className="flex justify-end mt-8">
        <motion.button
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          whileHover={disabled ? undefined : { scale: 1.05 }}
          whileTap={disabled ? undefined : { scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
          title={disabled ? lockHint : undefined}
          className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold rounded-lg border border-black/5 transition-all ${disabled ? "bg-slate-200 text-slate-400 cursor-not-allowed" : s.button}`}
        >
          {disabled ? <Lock size={16} strokeWidth={2.5} /> : <Icon size={16} strokeWidth={2.5} />}
          {cta}
        </motion.button>
      </div>
    </div>
  );
}

function Hub({ onPick, onLogout, user, canWrite = false }) {
  const firstName = (user?.name || user?.username || (user?.email?.split("@")[0] || "")).split(/[ .]/)[0];
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="min-h-screen bg-slate-200 relative overflow-hidden text-slate-900 font-sans">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-50/60 via-transparent to-transparent -z-10" />
      <div className="absolute top-40 -left-40 w-96 h-96 rounded-full bg-green-200/20 blur-3xl -z-10" />
      <div className="absolute top-72 -right-40 w-96 h-96 rounded-full bg-red-200/20 blur-3xl -z-10" />

      <PageNav label="REGISTRY" user={user} onLogout={onLogout} />

      <main className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        <section className={`${CARD} p-8 md:p-12`}>
          <header className="mb-10">
            <motion.h1
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter leading-[1.05] mb-6"
            >
              {greeting},{" "}
              {firstName ? (
                <span className="text-green-600">{firstName}</span>
              ) : (
                "there"
              )}.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="text-lg md:text-xl text-slate-600 font-medium leading-relaxed"
            >
              Welcome to the <span className="text-green-700 font-bold">NEED-Entry Registry</span>. From here you can register a new datasource into NEED Catalog, modify or
              delete one you no longer want exposed.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5"
            >
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">pyGeoAPI native</p>
                <p className="text-sm font-medium text-slate-700 leading-snug">
                  Point at an endpoint, add the metadata, and register it to the catalog.
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">Manage</p>
                <p className="text-sm font-medium text-slate-700 leading-snug">
                  Modify and remove datasources you no longer want exposed in the NEED Catalog.
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">Live activity log</p>
                <p className="text-sm font-medium text-slate-700 leading-snug">
                  Watch the activity log as a job runs to see exactly what reaches the catalog at each step.
                </p>
              </div>
            </motion.div>
          </header>

          {!canWrite && (
            <div className="mb-8 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
              <Lock size={16} className="text-amber-600 shrink-0" />
              <p className="text-sm font-medium text-amber-800">
                You have <span className="font-bold">read-only</span> access. Registering, modifying,
                and deregistering datasources require an <span className="font-bold">editor</span> or{" "}
                <span className="font-bold">administrator</span> role.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ActionCard
              title="REGISTER DATASOURCE"
              desc="Add a new dataset to the NEED-Catalog by walking through its OpenAPI endpoint and curated metadata."
              cta="Register"
              icon={FilePlus2}
              color="green"
              disabled={!canWrite}
              onClick={() => onPick("register")}
            />
            <ActionCard
              title="MODIFY DATASOURCE"
              desc="Edit a registered datasource: replace its OpenAPI document or update its metadata context."
              cta="Modify"
              icon={Settings2}
              color="amber"
              onClick={() => onPick("modify")}
            />
            <ActionCard
              title="DEREGISTER DATASOURCE"
              desc="Safely decommission and remove an active datasource from the registry and downstream caches."
              cta="Deregister"
              icon={Trash2}
              color="red"
              onClick={() => onPick("deregister")}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

export default function App() {
  const { logout, user } = useAuth();
  const canWrite = !!user?.can_write;
  const [mode, setMode] = useState("hub");

  const goHub = () => setMode("hub");

  // Datasources are published on behalf of an institution, so an unaffiliated user
  // gets the onboarding gate instead of the app until they belong to one.
  if (user?.onboarding_required) return <Onboarding />;

  // Register is gated at the Hub; Modify and Deregister are viewable by anyone.
  if (mode === "register" && canWrite) return <Register onBack={goHub} />;
  if (mode === "modify") return <Modify onBack={goHub} />;
  if (mode === "deregister") return <Deregister onBack={goHub} />;

  return <Hub onPick={setMode} onLogout={logout} user={user} canWrite={canWrite} />;
}
